import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import {
  shellCommandResultSchema,
  type DirectoryCommandPort,
  type DirectoryJobObservation,
  type PresentationCapabilityCommandPort,
  type PresentationCapabilityObservation,
  type ShellApprovalReview,
  type ShellCommandPort,
  type ShellDownload,
  type ShellSnapshot,
  type ShellStageRecord,
  type ShellSurface,
} from "@ppt-agent/contracts";
import {
  InlineFeedback,
  Skeleton,
  StatusBadge,
  UiButton,
} from "./design-system";
import { createShellViewModel, WORKFLOW_STEPS } from "./view-model";
import { ModelSettings, modelPreferencesFromSearch, useModelSelection, type ModelCatalogLoader } from "./model-settings";
import { readCreationDraft } from "./creation-draft";
import { FlowisleMark } from "./flowisle-mark";
import { FlowisleUiIcon } from "./workflow-icons";
import { MarkdownInput } from "./markdown-input";
import { GenerationProgressCard, STAGE_COPY, useGenerationProgress } from "./generation-progress";
import type { GenerationStage } from "@ppt-agent/contracts";

const activeStatuses = new Set([
  "GENERATING",
  "RENDERING",
  "EVALUATING",
  "REPAIRING",
  "EXPORTING",
]);

const activeDirectoryStatuses = new Set(["QUEUED", "RUNNING"]);

export interface PptAgentShellProps {
  surface: ShellSurface;
  commandPort: ShellCommandPort;
  modelCatalogLoader?: ModelCatalogLoader;
  directoryCommandPort?: DirectoryCommandPort;
  capabilityCommandPort?: PresentationCapabilityCommandPort;
  onDownload?: (download: ShellDownload) => void;
}

function bytesFromBase64(value: string): Uint8Array {
  const binary = globalThis.atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function saveDownload(download: ShellDownload): void {
  const bytes = bytesFromBase64(download.contentBase64);
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  const blob = new Blob([buffer], {
    type: download.mediaType,
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = download.fileName;
  // Keep the download navigation in the current user-gesture task. Some
  // browsers cancel a synthetic anchor click when the object URL is revoked
  // immediately after click(), which leaves the UI stuck at “preparing”.
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  window.setTimeout(() => {
    anchor.remove();
    URL.revokeObjectURL(url);
  }, 60_000);
}

function sourceNames(value: FormDataEntryValue | null): string[] {
  return String(value ?? "")
    .split(/[，,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => typeof globalThis.matchMedia === "function" && globalThis.matchMedia(query).matches);
  useEffect(() => {
    if (typeof globalThis.matchMedia !== "function") return;
    const media = globalThis.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener?.("change", update);
    return () => media.removeEventListener?.("change", update);
  }, [query]);
  return matches;
}
function projectIdFromName(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9\u4e00-\u9fff]+/g, "-")
    .replace(/^-|-$/g, "");
  return `project-${slug || "demo"}`;
}


type ShellSection = "workspace" | "history" | "delivery";
const WORKFLOW_STAGE_IDS = ["intent", "direction", "creation", "delivery"] as const;

function fallbackStageRecords(snapshot: ShellSnapshot, phase: number): ShellStageRecord[] {
  return WORKFLOW_STEPS.map((label, index) => ({
    stage: WORKFLOW_STAGE_IDS[index]!,
    label,
    status: index + 1 < phase ? "completed" : index + 1 === phase ? "current" : "pending",
    summary: index + 1 === phase ? snapshot.recentEvent : index + 1 < phase ? "本阶段已经完成" : "本阶段尚未开始",
    details: [index + 1 === phase ? snapshot.progress.label : "详细记录将在本阶段开始后显示"],
  }));
}

function WorkflowStrip({ records, selectedPhase, onSelect, label = "演示文稿工作流" }: { records: ShellStageRecord[]; selectedPhase?: number; onSelect: (phase: number) => void; label?: string }) {
  return (
    <ol className="workflow-strip" aria-label={label}>
      {records.map((record, index) => (
        <li key={record.stage} data-state={record.status} data-selected={selectedPhase === index + 1 ? "true" : undefined}>
          <button type="button" aria-current={record.status === "current" ? "step" : undefined} aria-label={`查看第 ${index + 1} 阶段：${record.label}`} onClick={() => onSelect(index + 1)}>
            <b>{index + 1}</b><span>{record.label}<small>{record.status === "completed" ? "完成" : record.status === "current" ? "当前" : "待处理"}</small></span>
          </button>
        </li>
      ))}
    </ol>
  );
}

function RunHistoryPage({ records, selectedPhase, onSelect }: { records: ShellStageRecord[]; selectedPhase: number; onSelect: (phase: number) => void }) {
  const record = records[selectedPhase - 1] ?? records[0];
  if (!record) return null;
  return (
    <section className="run-history-page" data-testid="run-history-page" aria-labelledby="history-title">
      <header className="run-history-heading"><div><span className="eyebrow">项目脉络</span><h1 id="history-title">运行记录</h1></div><span className="history-live-state"><i aria-hidden="true" />实时同步</span></header>
      <WorkflowStrip records={records} selectedPhase={selectedPhase} onSelect={onSelect} label="四阶段运行记录" />
      <article className="stage-record-panel" role="region" aria-live="polite" aria-labelledby="stage-record-title">
        <header><span>{String(selectedPhase).padStart(2, "0")}</span><div><small>{record.status === "completed" ? "已完成" : record.status === "current" ? "正在进行" : "尚未开始"}</small><h2 id="stage-record-title">{record.label}</h2></div></header>
        <p>{record.summary}</p>
        <ul>{record.details.map((detail, index) => <li key={index + "-" + detail}>{detail}</li>)}</ul>
      </article>
    </section>
  );
}

export function PptAgentShell({
  surface,
  commandPort,
  modelCatalogLoader,
  directoryCommandPort,
  capabilityCommandPort,
  onDownload,
}: PptAgentShellProps) {
  const creationPrefill = useMemo(() => {
    const search = surface === "web" && typeof globalThis.location !== "undefined" ? globalThis.location.search : "";
    const parameters = new URLSearchParams(search);
    const handoff = readCreationDraft(search);
    const input = handoff.draft?.input;
    const legacy = !parameters.has("draft");
    return {
      objective: input?.objective ?? (legacy ? parameters.get("objective") ?? "" : ""),
      sources: input?.sourceNames.join("，") ?? (legacy ? parameters.get("sources") ?? "" : ""),
      sourceNames: input?.sourceNames,
      preferences: input?.preferences ?? modelPreferencesFromSearch(search),
      hasPrefill: parameters.has("draft") || parameters.has("objective"),
      error: handoff.error,
    };
  }, [surface]);

  const modelSelection = useModelSelection(modelCatalogLoader, creationPrefill.preferences);
  const [creationObjective, setCreationObjective] = useState(creationPrefill.hasPrefill ? creationPrefill.objective : "请管理层批准下一季度的经营重点与资源配置");
  const creationProjectId = useRef<string | undefined>(undefined);
  const [snapshot, setSnapshot] = useState<ShellSnapshot>();
  const [directoryJob, setDirectoryJob] = useState<DirectoryJobObservation>();
  const [capability, setCapability] =
    useState<PresentationCapabilityObservation>();
  const [busy, setBusy] = useState(false);
  const generation = useGenerationProgress(commandPort);
  const thinking = generation.value?.status === "running";
  const progressPhase = thinking && generation.value ? STAGE_COPY[generation.value.stage].phase : undefined;
  const [, setMessage] = useState("准备创建项目");
  const [error, setError] = useState<string>();
  const [snapshotStale, setSnapshotStale] = useState(false);
  const [changeReason, setChangeReason] = useState("");
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const compactNavigation = useMediaQuery("(max-width: 760px)");
  const [activeSection, setActiveSection] = useState<ShellSection>(() => surface === "web" && globalThis.location?.hash === "#progress" ? "history" : "workspace");
  const [selectedWorkflowPhase, setSelectedWorkflowPhase] = useState<number>();
  const [agentOpen, setAgentOpen] = useState(true);
  const [creationMode, setCreationMode] = useState<"goal" | "sources">(() => creationPrefill.sources ? "sources" : "goal");
  const [selectedSlideId, setSelectedSlideId] = useState<string>();
  const selectedSlide = snapshot?.slides.find(({ slideId }) => slideId === selectedSlideId) ?? snapshot?.slides[0];
  const visibleSlides = surface === "windows" && selectedSlide ? [selectedSlide] : (snapshot?.slides ?? []);
  const selectedSlideIndex = selectedSlide ? (snapshot?.slides.findIndex(({ slideId }) => slideId === selectedSlide.slideId) ?? -1) : -1;
  const creationForm = useRef<HTMLFormElement>(null);
  const portableInput = useRef<HTMLInputElement>(null);
  const view = useMemo(
    () => (snapshot ? createShellViewModel(snapshot) : undefined),
    [snapshot],
  );
  const currentWorkflowPhase = progressPhase ?? view?.phase ?? 1;
  const stageRecords = useMemo(() => {
    if (!snapshot) return [];
    const records = snapshot.stageRecords ?? fallbackStageRecords(snapshot, currentWorkflowPhase);
    if (!thinking) return records;
    return records.map((record, index) => ({ ...record, status: index + 1 < currentWorkflowPhase ? "completed" as const : index + 1 === currentWorkflowPhase ? "current" as const : "pending" as const }));
  }, [snapshot, currentWorkflowPhase, thinking]);
  const visibleWorkflowPhase = selectedWorkflowPhase ?? currentWorkflowPhase;

  function navigateTo(section: ShellSection, phase?: number) {
    setActiveSection(section);
    if (phase) setSelectedWorkflowPhase(phase);
    setNavigationOpen(false);
    if (surface === "web" && typeof globalThis.history !== "undefined") {
      const hash = section === "history" ? "#progress" : section === "delivery" ? "#delivery" : "";
      globalThis.history.replaceState(null, "", globalThis.location.pathname + globalThis.location.search + hash);
    }
  }

  async function run<T>(operation: (operationId?: string) => Promise<T>, wait?: { projectId: string; stage: GenerationStage }): Promise<T | undefined> {
    const operationId = wait ? generation.start(wait.projectId, wait.stage) : undefined;
    setBusy(true);
    setError(undefined);
    try {
      const result = await operation(operationId);
      if (operationId) await generation.finish(operationId, true, wait?.stage === "assets" ? "package" : wait?.stage);
      return result;
    } catch (cause) {
      if (operationId) await generation.finish(operationId, false);
      if (wait && snapshot) {
        setSnapshotStale(true);
        try {
          const latest = await commandPort.execute({ type: "read_snapshot", projectId: wait.projectId });
          if (latest.kind === "snapshot") { setSnapshot(latest.value); setSnapshotStale(false); }
        } catch { /* Preserve the original failure; retry refreshes state first. */ }
      }
      setError(cause instanceof Error ? cause.message : "命令执行失败");
      return undefined;
    } finally {
      setBusy(false);
    }
  }


  useEffect(() => {
    if (!generation.detached || generation.value?.status !== "completed") return;
    let cancelled = false;
    const projectId = generation.value.projectId;
    const timer = window.setTimeout(() => {
      void commandPort.execute({ type: "inspect", projectId }).then((result) => {
        if (cancelled) return;
        if (result.kind === "snapshot") { setSnapshot(result.value); setMessage(result.value.recentEvent); setError(undefined); }
        generation.clearDetached();
      }).catch((cause: unknown) => {
        if (!cancelled) { setError(cause instanceof Error ? cause.message : "无法恢复已完成的任务"); generation.clearDetached(); }
      });
    }, 200);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [commandPort, generation.detached, generation.value?.status]);

  useEffect(() => {
    if (!snapshot || busy || !activeStatuses.has(snapshot.runStatus)) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      const operationId = generation.start(snapshot.projectId, "assets");
      void commandPort
        .execute({ type: "inspect", projectId: snapshot.projectId, operationId })
        .then((raw) => {
          if (cancelled) return;
          const result = shellCommandResultSchema.parse(raw);
          if (result.kind === "snapshot") {
            setSnapshot(result.value);
            setMessage(result.value.recentEvent);
            if (!activeStatuses.has(result.value.runStatus)) void generation.finish(operationId, true, result.value.deliveryReady ? "package" : undefined);
          }
        })
        .catch((cause: unknown) => {
          if (!cancelled) {
            void generation.finish(operationId, false);
            setError(cause instanceof Error ? cause.message : "无法刷新任务状态");
          }
        });
    }, 420);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [commandPort, snapshot, busy]);

  useEffect(() => {
    if (
      !directoryCommandPort ||
      !directoryJob ||
      !activeDirectoryStatuses.has(directoryJob.status)
    ) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void directoryCommandPort
        .execute({
          type: "inspect_directory_job",
          jobId: directoryJob.jobId,
        })
        .then((next) => {
          if (cancelled) return;
          setDirectoryJob(next);
          setMessage(next.summary);
        })
        .catch((cause: unknown) => {
          if (!cancelled) {
            setError(cause instanceof Error ? cause.message : "无法刷新目录任务");
          }
        });
    }, 260);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [directoryCommandPort, directoryJob]);

  useEffect(() => {
    if (!capabilityCommandPort) return;
    let cancelled = false;
    const directoryJobId =
      directoryJob?.status === "SUCCEEDED" ? directoryJob.jobId : undefined;
    void capabilityCommandPort
      .execute({
        type: "inspect_presentation_capabilities",
        input: {
          requiredFonts: ["Aptos Display", "Aptos"],
          ...(directoryJobId ? { directoryJobId } : {}),
        },
      })
      .then((next) => {
        if (cancelled) return;
        setCapability(next);
        if (next.status !== "supported") setMessage(next.summary);
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(
            cause instanceof Error ? cause.message : "无法检查本机演示能力",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [capabilityCommandPort, directoryJob]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      setNavigationOpen(false);
      if (surface === "windows") setAgentOpen(false);
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [surface]);

  async function refreshSnapshot() {
    if (!snapshot) return;
    const result = await run(() => commandPort.execute({ type: "read_snapshot", projectId: snapshot.projectId }));
    if (result?.kind === "snapshot") { setSnapshot(result.value); setSnapshotStale(false); }
  }
  async function retryPlanning() {
    if (!snapshot) return;
    const stage = snapshot.runStatus === "DESIGNING" ? "visual_direction" : "storyline";
    const result = await run((operationId) => commandPort.execute({ type: "retry_planning", projectId: snapshot.projectId, operationId }), { projectId: snapshot.projectId, stage });
    if (result?.kind === "snapshot") { setSnapshot(result.value); setMessage(result.value.recentEvent); }
  }
  async function retryDelivery() {
    if (!snapshot) return;
    const result = await run((operationId) => commandPort.execute({ type: "inspect", projectId: snapshot.projectId, operationId }),
      { projectId: snapshot.projectId, stage: "assets" });
    if (result?.kind === "snapshot") { setSnapshot(result.value); setMessage(result.value.recentEvent); }
  }

  async function handleSelectDirectory() {
    if (!directoryCommandPort || !creationForm.current) return;
    const form = new FormData(creationForm.current);
    const name = String(form.get("name") ?? "").trim();
    const objective = String(form.get("objective") ?? "").trim();
    if (!objective) {
      setError("请先填写这份演示要推动的决定。");
      return;
    }
    const projectId = projectIdFromName(name);
    setDirectoryJob(undefined);
    const next = await run(() =>
      directoryCommandPort.execute({
        type: "select_directory",
        input: {
          projectId,
          revisionId: `${projectId}-sources-v1`,
          query: objective,
        },
      }),
    );
    if (!next) return;
    setDirectoryJob(next);
    setMessage(next.summary);
  }

  async function handleCancelDirectory() {
    if (!directoryCommandPort || !directoryJob) return;
    const next = await run(() =>
      directoryCommandPort.execute({
        type: "cancel_directory_job",
        jobId: directoryJob.jobId,
      }),
    );
    if (!next) return;
    setDirectoryJob(next);
    setMessage(next.summary);
  }

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const name = String(form.get("name") ?? "").trim();
    const projectId = projectIdFromName(name);
    if (surface === "web") creationProjectId.current ??= projectId + "-" + globalThis.crypto.randomUUID().slice(0, 8);
    const creationId = surface === "web" ? creationProjectId.current! : projectId;
    const scannedSources =
      directoryJob?.status === "SUCCEEDED" && directoryJob.projectId === projectId
        ? (directoryJob.preview?.rankedSources ?? [])
            .map(({ relativePath }) => relativePath)
            .slice(0, 20)
        : undefined;
    const result = await run((operationId) =>
      commandPort.execute({
        type: "create_project", operationId,
        input: {
          projectId: creationId,
          idempotencyKey: `${creationId}-create-v1`,
          name,
          objective: String(form.get("objective") ?? ""),
          audience: String(form.get("audience") ?? ""),
          sourceNames: scannedSources ?? (creationPrefill.sourceNames && String(form.get("sources")) === creationPrefill.sources
            ? creationPrefill.sourceNames : sourceNames(form.get("sources"))),
          slideCount: Number(form.get("slideCount")),
          surface,
          ...modelSelection.preferences,
        },
      }), { projectId: creationId, stage: "brief" },
    );
    if (!result) return;
    const parsed = shellCommandResultSchema.parse(result);
    if (parsed.kind === "snapshot") {
      setSnapshot(parsed.value);
      setMessage(parsed.value.recentEvent);
    }
  }

  async function handleReview(status: ShellApprovalReview["status"]) {
    if (!snapshot?.pendingApproval) return;
    const pending = snapshot.pendingApproval;
    const result = await run((operationId) =>
      commandPort.execute({
        type: "review_approval", operationId,
        input: {
          projectId: snapshot.projectId,
          gate: pending.gate,
          artifactHash: pending.artifactHash,
          artifactRevision: pending.artifactRevision,
          status,
          ...(status === "changes_requested"
            ? { reason: changeReason || "请调整重点和表达顺序" }
            : {}),
        },
      }), { projectId: snapshot.projectId, stage: status === "changes_requested" ? pending.gate : pending.gate === "brief" ? "storyline" : pending.gate === "storyline" ? "visual_direction" : "assets" },
    );
    if (!result) return;
    const parsed = shellCommandResultSchema.parse(result);
    if (parsed.kind === "snapshot") {
      setSnapshot(parsed.value);
      setChangeReason("");
      setMessage(parsed.value.recentEvent);
    }
  }

  async function handleExport(type: "export_pptx" | "export_portable") {
    if (!snapshot) return;
    const result = await run(() =>
      commandPort.execute({ type, projectId: snapshot.projectId }),
    );
    if (!result) return;
    const parsed = shellCommandResultSchema.parse(result);
    if (parsed.kind !== "download") return;
    if (onDownload) onDownload(parsed.value);
    else saveDownload(parsed.value);
    setMessage(`${parsed.value.fileName} 已准备，校验值 ${parsed.value.sha256.slice(0, 18)}…`);
  }

  async function handlePortableFile(file: File | undefined) {
    if (!file) return;
    const bytes = new Uint8Array(await file.arrayBuffer());
    let binary = "";
    for (const byte of bytes) binary += String.fromCharCode(byte);
    const result = await run(() =>
      commandPort.execute({
        type: "open_portable",
        contentBase64: globalThis.btoa(binary),
        surface: "windows",
      }),
    );
    if (!result) return;
    const parsed = shellCommandResultSchema.parse(result);
    if (parsed.kind === "snapshot") {
      setSnapshot(parsed.value);
      setMessage("已从 Web 项目包继续，确认记录和版本保持不变。");
    }
  }

  return (
    <div className={`ppt-shell ppt-shell--${surface} ${sidebarCollapsed ? "ppt-shell--navigation-collapsed" : ""}`} data-surface={surface}>
      <header className="shell-topbar">
        <div className="topbar-leading">
          <button
            className="icon-button shell-navigation-toggle"
            type="button"
            aria-controls="shell-navigation"
            aria-expanded={compactNavigation ? navigationOpen : !sidebarCollapsed}
            aria-label={compactNavigation ? (navigationOpen ? "关闭项目导航" : "打开项目导航") : (sidebarCollapsed ? "展开侧边栏" : "收起侧边栏")}
            onClick={() => compactNavigation ? setNavigationOpen((value) => !value) : setSidebarCollapsed((value) => !value)}
          >
            <FlowisleUiIcon name="panel" />
          </button>
          <a className="shell-brand-home" href={surface === "web" ? "/" : "#"} aria-label="Flowisle PPT 首页"><FlowisleMark /></a>
          <strong>Flowisle PPT</strong>
          {surface === "windows" ? <span className="surface-badge">Windows 工作台</span> : null}
          {snapshot ? <span className="project-title">{snapshot.projectName}</span> : null}
        </div>
        <div className="topbar-actions">
          {surface === "windows" && snapshot ? (
            <UiButton
              size="compact"
              onClick={() => void handleExport("export_pptx")}
              disabled={!snapshot.deliveryReady}
              loading={busy}
              loadingLabel="正在导出"
            >
              导出 PPTX
            </UiButton>
          ) : null}
        </div>
      </header>

      <aside
        id="shell-navigation"
        className={`shell-navigation ${navigationOpen ? "is-open" : ""} ${sidebarCollapsed ? "is-collapsed" : ""}`}
        aria-label="项目导航"
      >
        <UiButton
          className="new-project"
          disabled={busy || thinking}
          variant="primary"
          leadingIcon={<FlowisleUiIcon name="plus" />}
          onClick={() => {
            setSnapshot(undefined);
            generation.reset(); setSnapshotStale(false);
            creationProjectId.current = undefined;
            setError(undefined);
            setMessage("准备创建项目");
            setActiveSection("workspace");
          }}
        >
          新建项目
        </UiButton>
        <nav aria-label="主要导航">
          <button type="button" aria-current={activeSection === "workspace" ? "page" : undefined} onClick={() => navigateTo("workspace")}>
            <FlowisleUiIcon name="workspace" /><span className="nav-label">{surface === "windows" ? "编辑器" : "工作区"}</span>
          </button>
          <button type="button" aria-current={activeSection === "history" ? "page" : undefined} onClick={() => navigateTo("history", visibleWorkflowPhase)}>
            <FlowisleUiIcon name="history" /><span className="nav-label">运行记录</span>
          </button>
          <button type="button" aria-current={activeSection === "delivery" ? "page" : undefined} onClick={() => navigateTo("delivery", 7)}>
            <FlowisleUiIcon name="delivery" /><span className="nav-label">交付文件</span>
          </button>
        </nav>
        {snapshot ? (
          <section className="nav-project" aria-label="当前项目">
            <small>当前项目</small>
            <strong>{snapshot.projectName}</strong>
            <span>{view?.statusLabel}</span>
          </section>
        ) : null}
      </aside>

      {navigationOpen ? <button className="navigation-scrim" aria-label="关闭项目导航" type="button" onClick={() => setNavigationOpen(false)} /> : null}

      <main id="workspace" className="shell-main">
        {generation.value ? <GenerationProgressCard value={generation.value} now={generation.now} disconnected={generation.disconnected} /> : null}
        {!snapshot ? (
          <section className="creation-panel" aria-labelledby="create-heading" hidden={thinking}>
            <header className="creation-heading">
              <StatusBadge tone="brand" dot>{surface === "windows" ? "Windows 专业创作" : "免安装轻量创作"}</StatusBadge>
              <h1 id="create-heading">{surface === "windows" ? "把项目资料变成专业演示" : "从一个清晰目标开始"}</h1>
              <p>{surface === "windows" ? "选择可信资料，由 Flowisle Agent 生成、校验并交付可编辑的 PowerPoint。" : "描述要推动的决定，先确认任务书，再逐步生成可编辑演示。"}</p>
              <div className="creation-assurance" aria-label="创作保障">
                <span>三次关键确认</span>
                <span>来源范围可控</span>
                <span>原生对象可编辑</span>
              </div>
            </header>
            <div className="creation-mode-tabs" role="tablist" aria-label="选择创建起点">
              <button
                type="button"
                role="tab"
                aria-selected={creationMode === "goal"}
                data-selected={creationMode === "goal" ? "true" : "false"}
                onClick={() => setCreationMode("goal")}
              >
                <span aria-hidden="true">01</span>
                <b>从目标开始</b>
                <small>一句话说明要推动的决定</small>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={creationMode === "sources"}
                data-selected={creationMode === "sources" ? "true" : "false"}
                onClick={() => setCreationMode("sources")}
              >
                <span aria-hidden="true">02</span>
                <b>从资料开始</b>
                <small>{surface === "windows" ? "选择本地项目目录" : "填写已授权资料"}</small>
              </button>
            </div>
            <form ref={creationForm} className="creation-form" data-mode={creationMode} onSubmit={(event) => void handleCreate(event)}>
              {creationPrefill.error ? <InlineFeedback className="wide" tone="danger" title="无法恢复草稿">{creationPrefill.error} <a href="/">返回官网</a></InlineFeedback> : null}
              {error ? <InlineFeedback className="wide" tone="danger" title="暂时无法继续">{error}</InlineFeedback> : null}
              <label className="compact-field">项目名称<input name="name" required defaultValue={creationPrefill.objective ? "Flowisle 新演示" : "季度经营决策"} /></label>
              <div className="wide intent-field">
                <span>希望这份演示推动什么决定？<small>先说明目标，Flowisle Agent 会在生成前与你确认任务书、故事线和视觉方向。</small></span>
                <MarkdownInput label="希望这份演示推动什么决定？" required value={creationObjective} onChange={setCreationObjective} placeholder="例如：让管理层批准下一季度的经营重点与资源配置" />
              </div>
              <label className="compact-field">受众<input name="audience" required defaultValue="管理层与业务负责人" /></label>
              <label className="compact-field">页数<input name="slideCount" type="number" min="3" max="30" defaultValue="6" required /></label>
              {directoryCommandPort ? (
                <section className="directory-picker wide" aria-label="项目目录安全导入">
                  <div className="directory-picker-heading">
                    <div>
                      <strong>项目目录</strong>
                      <small>由 Windows 选择目录；应用只展示相对路径和隐私决策，不向界面暴露绝对路径或文件内容。</small>
                    </div>
                    <div className="directory-picker-actions">
                      <button
                        className="button button-secondary"
                        data-testid="select-directory"
                        type="button"
                        disabled={busy}
                        onClick={() => void handleSelectDirectory()}
                      >
                        {directoryJob ? "重新选择目录" : "选择项目目录"}
                      </button>
                      {directoryJob && activeDirectoryStatuses.has(directoryJob.status) ? (
                        <button
                          className="button button-secondary"
                          type="button"
                          disabled={busy}
                          onClick={() => void handleCancelDirectory()}
                        >
                          取消扫描
                        </button>
                      ) : null}
                    </div>
                  </div>
                  {directoryJob ? (
                    <div
                      className="directory-preview"
                      data-job-id={directoryJob.jobId}
                      data-status={directoryJob.status}
                      data-testid="directory-privacy-preview"
                    >
                      <header>
                        <span>{directoryJob.rootLabel}</span>
                        <b>{directoryJob.summary}</b>
                      </header>
                      {directoryJob.preview ? (
                        <>
                          <p data-testid="directory-counts">
                            安全纳入 {directoryJob.preview.counts.included} · 排除 {directoryJob.preview.counts.excluded} · 阻止 {directoryJob.preview.counts.blocked}
                          </p>
                          <div className="directory-preview-grid">
                            <section>
                              <h3>相关资料（创建时取前 20 项）</h3>
                              <ol>
                                {directoryJob.preview.rankedSources.slice(0, 20).map((source) => (
                                  <li key={source.relativePath}><span>{source.relativePath}</span><small>相关度 {source.score}</small></li>
                                ))}
                              </ol>
                            </section>
                            <section>
                              <h3>隐私与安全决策</h3>
                              <ul className="directory-decisions">
                                {directoryJob.preview.decisions.map((decision) => (
                                  <li key={decision.relativePath} data-decision={decision.decision}>
                                    <span>{decision.relativePath}</span>
                                    <small>{decision.decision}{decision.reason ? ` · ${decision.reason}` : ""}</small>
                                  </li>
                                ))}
                              </ul>
                            </section>
                          </div>
                        </>
                      ) : <p>{directoryJob.summary}</p>}
                    </div>
                  ) : null}
                </section>
              ) : null}
              {capabilityCommandPort ? (
                <section
                  className="capability-panel wide"
                  data-status={capability?.status ?? "loading"}
                  data-testid="presentation-capabilities"
                >
                  <header>
                    <div>
                      <strong>本机交付能力</strong>
                      <small>{capability?.summary ?? "正在检查 Office、字体和品牌资产…"}</small>
                    </div>
                    <b>{capability ? { supported: "已就绪", degraded: "降级", unavailable: "不可用" }[capability.status] : "检查中"}</b>
                  </header>
                  {capability ? (
                    <div className="capability-grid">
                      <section>
                        <h3>渲染与交付</h3>
                        <ul>
                          {capability.providers.map((provider) => (
                            <li key={provider.providerId}>
                              <span>{provider.providerId === "powerpoint" ? "PowerPoint" : "LibreOffice"}</span>
                              <small>{provider.available ? `${provider.providerVersion} · ${provider.confidence}` : provider.unavailableReason}</small>
                            </li>
                          ))}
                        </ul>
                      </section>
                      <section>
                        <h3>字体</h3>
                        <p>已发现 {capability.fonts.familyCount} 个字体族</p>
                        <small>{capability.fonts.missing.length ? `缺少：${capability.fonts.missing.join("、")}` : "Aptos Display / Aptos 已就绪"}</small>
                      </section>
                      <section>
                        <h3>品牌资产</h3>
                        {capability.brand.assets.length ? (
                          <ul>{capability.brand.assets.map((asset) => <li key={asset.relativePath}><span>{asset.relativePath}</span><small>{asset.kind}</small></li>)}</ul>
                        ) : <p>未发现已命名的 logo、模板、主题或品牌指南。</p>}
                      </section>
                      {capability.findings.length ? (
                        <section className="capability-findings">
                          <h3>兼容性与恢复</h3>
                          <ul>{capability.findings.map((finding, index) => <li key={`${finding.code}-${index}`} data-severity={finding.severity}><span>{finding.summary}</span><small>{finding.recoveryAction}</small></li>)}</ul>
                        </section>
                      ) : null}
                    </div>
                  ) : null}
                </section>
              ) : null}
              <label className="wide source-field">已授权资料<input name="sources" defaultValue={creationPrefill.hasPrefill ? creationPrefill.sources : "经营数据.xlsx，访谈纪要.docx"} aria-describedby="source-hint" /><small id="source-hint">仅填写已明确授权使用的文件；目录扫描成功时优先使用相关度最高的 20 个安全资料。</small></label>
              <ModelSettings selection={modelSelection} className="wide" />
              <UiButton
                className="submit-project"
                variant="primary"
                size="large"
                type="submit"
                loading={busy}
                loadingLabel="正在理解你的目标"
                disabled={modelSelection.loading || Boolean(modelSelection.error) || Boolean(directoryJob && activeDirectoryStatuses.has(directoryJob.status))}
              >
                生成任务书
              </UiButton>
            </form>
          </section>
        ) : (
          <>
            {activeSection === "history" ? (
              <RunHistoryPage records={stageRecords} selectedPhase={visibleWorkflowPhase} onSelect={setSelectedWorkflowPhase} />
            ) : activeSection === "delivery" ? (
              <section className="standalone-delivery" data-testid="delivery-page" aria-labelledby="standalone-delivery-title">
                <header><span className="eyebrow">第四阶段</span><h1 id="standalone-delivery-title">交付文件</h1></header>
                {snapshot.deliveryReady ? (
                  <section className="delivery-panel" aria-labelledby="delivery-title">
                    <div><h2 id="delivery-title">可编辑文件已准备</h2><p>原生文字、图表、表格、页码和备注保持可编辑。</p></div>
                    <div><UiButton variant="primary" loading={busy} loadingLabel="正在准备" onClick={() => void handleExport("export_pptx")}>下载 PPTX</UiButton><UiButton disabled={busy} onClick={() => void handleExport("export_portable")}>下载项目包</UiButton></div>
                  </section>
                ) : (
                  <article className="stage-record-panel"><header><span>04</span><div><small>尚未完成</small><h2>文件仍在准备</h2></div></header><p>{stageRecords[3]?.summary ?? snapshot.recentEvent}</p></article>
                )}
              </section>
            ) : (
              <>
                <WorkflowStrip records={stageRecords} onSelect={(phase) => navigateTo("history", phase)} />
            <div className="workspace-grid">
              {surface === "windows" && snapshot.slides.length ? (
                <aside className="filmstrip" aria-label="幻灯片缩略图">
                  {snapshot.slides.map((slide) => (
                    <button
                      key={slide.slideId}
                      type="button"
                      data-selected={selectedSlide?.slideId === slide.slideId ? "true" : "false"}
                      aria-current={selectedSlide?.slideId === slide.slideId ? "page" : undefined}
                      aria-label={`第 ${slide.order} 页：${slide.title}`}
                      onClick={() => setSelectedSlideId(slide.slideId)}
                    >
                      <b>{slide.order}</b>
                      <span>{slide.title}</span>
                      <small>{slide.stateLabel}</small>
                    </button>
                  ))}
                </aside>
              ) : null}

              <section className="primary-workspace">
                {!thinking && snapshotStale ? <section className="planning-recovery" aria-label="刷新项目状态">
                  <p>暂时无法确认最新状态，请刷新后继续。</p>
                  <UiButton onClick={() => void refreshSnapshot()} disabled={busy}>刷新状态</UiButton>
                </section> : null}
                {!thinking && !snapshotStale && ["PLANNING", "DESIGNING"].includes(snapshot.runStatus) ? (
                  <section className="planning-recovery" aria-label="恢复生成">
                    <p>{error ?? "上次生成未完成，已保留确认的内容。"}</p>
                    <UiButton variant="primary" disabled={busy} onClick={() => void retryPlanning()}>重试此阶段</UiButton>
                  </section>
                ) : null}
                {!thinking && !snapshotStale && snapshot.runStatus === "NEEDS_ATTENTION" ? (
                  <section className="planning-recovery" aria-label="恢复交付生成">
                    <p>{error ?? snapshot.recentEvent}</p>
                    {snapshot.recovery?.retryable ? (
                      <UiButton variant="primary" disabled={busy} onClick={() => void retryDelivery()}>继续生成</UiButton>
                    ) : null}
                  </section>
                ) : null}
                {snapshot.pendingApproval && !thinking && !snapshotStale ? (
                  <article className="approval-card" data-testid="approval-review" aria-labelledby="approval-title">
                    <header><div><span className="review-label">需要你的确认 · 版本 {snapshot.pendingApproval.artifactRevision}</span><h2 id="approval-title">{snapshot.pendingApproval.title}</h2></div><span className="hard-gate">硬确认点</span></header>
                    <p className="approval-summary">{snapshot.pendingApproval.summary}</p>
                    <ul>{snapshot.pendingApproval.details.map((detail) => <li key={detail}>{detail}</li>)}</ul>
                    <label className="change-input">调整意见（可选）<input value={changeReason} onChange={(event) => setChangeReason(event.target.value)} placeholder="说明需要改变的重点" /></label>
                    <footer>
                      <UiButton disabled={busy} onClick={() => void handleReview("changes_requested")}>要求调整</UiButton>
                      <UiButton variant="primary" loading={busy} loadingLabel="正在继续" onClick={() => void handleReview("approved")}>批准并继续</UiButton>
                    </footer>
                  </article>
                ) : null}

                {activeStatuses.has(snapshot.runStatus) && !snapshot.pendingApproval && !generation.value ? (
                  <article className="processing-card" aria-live="polite" aria-label="Flowisle Agent 正在处理">
                    <div className="processing-orbit" aria-hidden="true"><i /><i /><i /></div>
                    <StatusBadge tone="brand" dot>Agent 正在工作</StatusBadge>
                    <h2>{snapshot.progress.label}</h2>
                    <p>{snapshot.recentEvent}</p>
                    <div className="processing-skeleton">
                      <Skeleton width="86%" />
                      <Skeleton width="64%" />
                      <Skeleton width="74%" />
                    </div>
                  </article>
                ) : null}

                {snapshot.slides.length ? (
                  <>
                    {surface === "windows" && selectedSlide ? (
                      <header className="canvas-toolbar" aria-label="画布导航">
                        <div>
                          <button className="icon-button" type="button" aria-label="上一页" disabled={selectedSlideIndex <= 0} onClick={() => setSelectedSlideId(snapshot.slides[selectedSlideIndex - 1]?.slideId)}>←</button>
                          <span>{selectedSlide.order} / {snapshot.slides.length}</span>
                          <button className="icon-button" type="button" aria-label="下一页" disabled={selectedSlideIndex >= snapshot.slides.length - 1} onClick={() => setSelectedSlideId(snapshot.slides[selectedSlideIndex + 1]?.slideId)}>→</button>
                        </div>
                        <span>适合窗口 · 100%</span>
                      </header>
                    ) : null}
                    <section className={`slide-preview-grid ${surface === "windows" ? "is-canvas" : ""}`} aria-label={surface === "windows" ? "幻灯片编辑画布" : "幻灯片预览"}>
                    {visibleSlides.map((slide) => (
                      <article className="slide-preview" data-selected={surface === "windows" ? "true" : undefined} key={slide.slideId} aria-label={`第 ${slide.order} 页 ${slide.title}`}>
                        <span className="slide-kicker">{slide.order === 1 ? "DECISION BRIEF" : `SECTION ${String(slide.order).padStart(2, "0")}`}</span>
                        <h2>{slide.title}</h2><p>{slide.purpose}</p><small>{slide.order}</small>
                      </article>
                    ))}
                    </section>
                  </>
                ) : null}

                {snapshot.deliveryReady ? (
                  <section id="delivery" className="delivery-panel" aria-labelledby="delivery-title">
                    <div><span className="eyebrow">交付</span><h2 id="delivery-title">可编辑文件已准备</h2><p>原生文字、图表、表格、页码和备注保持可编辑。</p></div>
                    <div>
                      <UiButton variant="primary" loading={busy} loadingLabel="正在准备" onClick={() => void handleExport("export_pptx")}>下载 PPTX</UiButton>
                      <UiButton disabled={busy} onClick={() => void handleExport("export_portable")}>下载项目包</UiButton>
                    </div>
                  </section>
                ) : null}
              </section>

              {surface === "windows" ? (
                <aside className={`agent-panel ${agentOpen ? "is-open" : ""}`} aria-label="Flowisle Agent 协作面板">
                  <header><strong>Flowisle Agent</strong><button className="icon-button" type="button" aria-expanded={agentOpen} aria-label={agentOpen ? "收起 Agent 面板" : "展开 Agent 面板"} onClick={() => setAgentOpen((value) => !value)}>{agentOpen ? "→" : "←"}</button></header>
                  {agentOpen ? <><p>{snapshot.recentEvent}</p><section><small>确认记录</small>{snapshot.approvals.map((approval) => <div className="approval-row" key={approval.gate}><span>{approval.label}</span><b>{approval.status === "completed" ? "已确认" : approval.status === "current" ? "待确认" : "未开始"}</b></div>)}</section><UiButton className="portable-open" onClick={() => portableInput.current?.click()}>打开 Web 项目包</UiButton></> : null}
                </aside>
              ) : null}
            </div>
              </>
            )}
          </>
        )}
      </main>

      <input ref={portableInput} className="sr-only" type="file" accept=".json,.pptagent" onChange={(event) => void handlePortableFile(event.currentTarget.files?.[0])} />
      <div
        className="shell-status"
        role="status"
        aria-live="polite"
        data-tone="danger"
        data-visible={error && !(snapshot && !thinking && (snapshotStale || ["PLANNING", "DESIGNING"].includes(snapshot.runStatus))) ? "true" : "false"}
      >
        {error ? `错误：${error}` : null}
      </div>
    </div>
  );
}
